/**
 * User — the person, at `users/{uid}`.
 *
 * Created on first member sign-in, alongside consent capture. A BUYER NEVER
 * REACHES THIS COLLECTION: buyers hold anonymous sessions and no document is
 * ever written for them, which is what makes this collection member-only
 * without a discriminant field — and what keeps buyer PII out of Firestore.
 *
 * Self-only: a member reads and writes their own doc and no other. An admin
 * rendering the member list reads the Member mirror instead.
 *
 * Fields justified:
 *   uid            — doc id, mirrored so a parsed doc identifies itself.
 *   email          — the member sign-in address; also mirrored onto Member.
 *   displayName    — attribution: who scanned, who issued the refund.
 *   legalConsents  — versioned, timestamped acceptance; the record a German
 *                    consent regime expects to be able to produce.
 *   language       — member UI locale; absent falls back to the browser.
 *                    A stored value outside {de, en} coerces to null on
 *                    parse (BCP-47 prefix match) rather than throwing.
 *   activeEventId  — the event last chosen in the shell, so the choice
 *                    survives a reload. The persisted counterpart of the
 *                    actor's `eventId`.
 *
 * Excluded:
 *   permissions / workspaces — in-memory session projections of the grant
 *                    subcollection, never persisted; the grants are the authority.
 *   roleId         — the grant owns it; mirroring here would drift.
 *   memberships    — resolved from grants; saved workspaceId is context only.
 *   mfa            — an ID-token claim, not document data.
 */

import { entity } from "@mhmo91/ican";
import { firestore } from "@mhmo91/ican/firestore/binding";
import { LocaleEnum } from "@momo/i18n";
import type { Timestamp } from "firebase/firestore";
import { z } from "zod";
import { type Actor, Id } from "./workspace.entity.ts";

/**
 * LegalConsent — one accepted document version, appended to the user doc.
 *
 * Fields justified:
 *   kind        — which document was accepted.
 *   version     — the doc version accepted; a new version demands a new consent.
 *   acceptedAt  — when; the proof half of the record.
 *   userAgent   — the client that clicked; corroborates the record.
 *   platform    — true when the text accepted is the deployment operator's
 *                 own (`platform/legal`) — the bare host's consent, or a
 *                 founding invitation's accept — rather than an
 *                 organization's: the two parties' versions share one
 *                 vocabulary, so the record names whose text it was.
 */
export const LegalConsentSchema = z.object({
	kind: z.enum(["tos", "privacy"]),
	version: z
		.string()
		.min(1)
		.describe("date-stamped document version, e.g. 2026-05-10"),
	acceptedAt: z.custom<Timestamp>(),
	userAgent: z.string(),
	platform: z
		.boolean()
		.nullish()
		.describe("the operator's platform text, not an organization's"),
});
export type LegalConsent = z.infer<typeof LegalConsentSchema>;

export const UserSchema = z.object({
	uid: Id,
	email: z.string().email(),
	displayName: z.string().min(1),
	legalConsents: z.array(LegalConsentSchema).min(1),
	language: z.preprocess((value) => {
		if (typeof value !== "string") return null;
		const lower = value.toLowerCase();
		if (lower.startsWith("de")) return "de";
		if (lower.startsWith("en")) return "en";
		return null;
	}, LocaleEnum.nullish()),
	activeEventId: Id.nullish().describe(
		"last event chosen in the shell; null before a first choice",
	),
	workspaceId: Id.nullish().describe(
		"remembered organization selection; grants remain the authority",
	),
	createdAt: z.custom<Timestamp>(),
	updatedAt: z.custom<Timestamp>(),
});
export type User = z.infer<typeof UserSchema>;

// Self-only. `read` uses pathVar('uid') so the rule passes for a
// not-yet-existing doc — first sign-in reads before the create. `create`
// keys on the doc's own uid. mfa is NOT required: a member account
// mid-enrollment must reach its own doc (and record consent) before the
// claim flips true.
//
// `update` is deliberately NOT declared. A generated update rule maps 1:1
// to `allow update` with no field mask, which would let a member rewrite
// their own `email`, `createdAt`, and — worse — shrink `legalConsents`,
// destroying the consent record this model treats as evidence. The
// write-pattern form cannot help: its mask is derived from pinned values
// (`affectedKeys().hasOnly(...)` alongside `data.X == literal`), and a
// display-name edit pins no value.
//
// So the update rule is a hand fragment (`extraMatches`), carrying what
// the generator cannot express — an affected-keys mask with no value
// pinning, plus an append-only guard on the consent array. See root
// `package.json#ican.extraMatches`.
export const User = entity<Actor>()({
	schema: UserSchema,
	actions: ["read", "create"] as const,
	namespace: "tickets.users",
})
	.pipe(({ can, pathVar }) => [
		can("read").when((_data, me) => me.uid.eq(pathVar("uid"))),
		can("create").when((data, me) => data.uid.eq(me.uid)),
	])
	.adaptor(firestore({ hierarchicalGrants: true, path: "users/{uid}" }));
